/**
 * routingApi.ts — Trip-planner routing + suggestions client.
 *
 * Pairs with the GraphHopper-backed /api/routing/plan and /api/routing/suggestions
 * endpoints. The server caches the plan under route_id; we re-send route_id
 * when fetching suggestions to avoid recomputing the same polyline.
 */

import { getUserToken } from './reviewerApi';

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

function authHeaders(): HeadersInit {
  const token = getUserToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface DailyAnchor {
  day: number;             // 1-based
  lat: number;
  lng: number;
  cumulative_km?: number;
  cumulative_minutes?: number;
}

export interface PlanRouteResponse {
  route_id: string;
  distance_km: number;
  duration_minutes: number;
  polyline: LatLng[];
  daily_anchors: DailyAnchor[];
  waypoints: LatLng[];
  nights: number;
  capped?: boolean;
}

export interface PlanRouteRequest {
  start: LatLng;
  end: LatLng;
  waypoints?: LatLng[];   // ordered intermediate stops the route is drawn through
  vehicle_id?: string;
  vehicle_profile?: string;
  max_daily_hours?: number;
  avoid?: string[];
}

// The server returns polyline/anchors as [lat, lng] pairs; normalise to {lat,lng}.
function toLatLng(p: unknown): LatLng | null {
  if (Array.isArray(p) && p.length >= 2) return { lat: Number(p[0]), lng: Number(p[1]) };
  if (p && typeof p === 'object' && 'lat' in p && 'lng' in p) {
    const o = p as { lat: number; lng: number };
    return { lat: Number(o.lat), lng: Number(o.lng) };
  }
  return null;
}

/**
 * Routing is signed-in only (GraphHopper bills per request; the endpoints
 * 401 without a token). Thrown BEFORE any network call so a guest page never
 * fires a doomed request.
 */
export class RoutingSignInRequired extends Error {
  constructor() {
    super('Sign in to plan the route');
    this.name = 'RoutingSignInRequired';
  }
}

export function canUseRouting(): boolean {
  return !!getUserToken();
}

export async function planRoute(req: PlanRouteRequest): Promise<PlanRouteResponse> {
  if (!canUseRouting()) throw new RoutingSignInRequired();
  const res = await fetch(`${API_BASE}/routing/plan`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Routing failed (${res.status}) ${txt}`);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Routing failed');
  const d = json.data || {};
  return {
    route_id: d.route_id,
    distance_km: typeof d.distance_km === 'number' ? d.distance_km : (d.distance_m ?? 0) / 1000,
    duration_minutes: typeof d.duration_minutes === 'number' ? d.duration_minutes : Math.round((d.duration_ms ?? 0) / 60000),
    polyline: (d.polyline || []).map(toLatLng).filter(Boolean) as LatLng[],
    daily_anchors: (d.daily_anchors || []).map((a: { day: number; lat: number; lng: number; cumulative_km?: number }) => ({
      day: a.day, lat: Number(a.lat), lng: Number(a.lng), cumulative_km: a.cumulative_km,
    })),
    waypoints: (d.waypoints || []).map(toLatLng).filter(Boolean) as LatLng[],
    nights: d.nights ?? Math.max(0, (d.daily_anchors || []).length),
    capped: d.capped,
  };
}

// ── Vehicle profiles ───────────────────────────────────────────────────────
export interface Vehicle {
  id: string;
  name: string;
  type: string; // sedan | 4wd | caravan | motorhome
  is_default?: boolean;
  /** The owner's own figure, when they've entered it (Account → Vehicles). */
  fuel_economy_l100km?: number | null;
}

export async function listVehicles(): Promise<Vehicle[]> {
  if (!getUserToken()) return [];
  try {
    const res = await fetch(`${API_BASE}/vehicles`, { headers: authHeaders() });
    if (!res.ok) return [];
    const json = await res.json();
    return json?.success && Array.isArray(json.data) ? json.data : [];
  } catch {
    return [];
  }
}

export async function getDefaultVehicle(): Promise<Vehicle | null> {
  const list = await listVehicles();
  if (!list.length) return null;
  return list.find((v) => v.is_default) || list[0];
}

// ── Trip polyline (multi-waypoint, vehicle-aware) ──────────────────────────
export async function tripPolyline(points: LatLng[], vehicleProfile?: string): Promise<LatLng[] | null> {
  if (!getUserToken() || points.length < 2) return null;
  try {
    const res = await fetch(`${API_BASE}/routing/trip-polyline`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ points, vehicle_profile: vehicleProfile || 'car' }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json?.success) return null;
    return ((json.data?.polyline || []).map(toLatLng).filter(Boolean)) as LatLng[];
  } catch {
    return null;
  }
}

export interface SuggestionCandidate {
  entity_id: number;
  name: string;
  lat: number;
  lng: number;
  detour_km?: number;
  features?: string[];
  is_free?: boolean;
  has_powered?: boolean;
  rating?: number;
  image_src?: string;
  score?: number;
  town?: string;
  state?: string;
  /** Server fields (route_suggestions.py). */
  suburb?: string | null;
  entity_type?: string | null;
  min_price?: number | null;
  distance_km?: number;
  booking_url?: string | null;
  has_partner_booking?: boolean;
}

export interface SuggestionDay {
  day: number;
  anchor: LatLng;
  date?: string;
  candidates: SuggestionCandidate[];
}

export interface SuggestionsResponse {
  per_day: SuggestionDay[];
}

export interface SuggestionsRequest {
  route_id?: string;
  daily_anchors?: DailyAnchor[];
  dates?: { start?: string; end?: string };
  prefs?: {
    prefer_free?: boolean;
    prefer_powered?: boolean;
    /** 'caravan' / 'motorhome' → caravan parks + campgrounds, powered first. */
    vehicle_type?: string;
    [key: string]: unknown;
  };
  radius_km?: number;
  /** Candidates per night, 1–5 (server default 3). */
  limit?: number;
}

export async function fetchSuggestions(
  req: SuggestionsRequest
): Promise<SuggestionsResponse> {
  if (!canUseRouting()) throw new RoutingSignInRequired();
  const res = await fetch(`${API_BASE}/routing/suggestions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Suggestions failed (${res.status}) ${txt}`);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Suggestions failed');
  return json.data as SuggestionsResponse;
}

export interface ApplySuggestionsSelection {
  day: number;
  entity_id: number;
}

export async function applySuggestions(
  tripId: string,
  selections: ApplySuggestionsSelection[]
): Promise<void> {
  const res = await fetch(`${API_BASE}/trips/${tripId}/apply-suggestions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ selections }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Apply suggestions failed (${res.status}) ${txt}`);
  }
}
